import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { storageBaseUrl } from '../../api';

const fmt = (n) => Number(n || 0).toLocaleString();
const docNo = (prefix, id, date) => {
    const d = date ? new Date(date) : new Date();
    return `${prefix}-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}-${String(id).padStart(3,'0')}`;
};
const fmtDate = (val) => {
    if (!val) return '';
    try { return new Date(val).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }); }
    catch { return val; }
};

const PrinterIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 6 2 18 2 18 9" />
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
        <rect x="6" y="14" width="12" height="8" />
    </svg>
);

const ArrowLeft = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
    </svg>
);

const EstimateViewPage = () => {
    const { projectId } = useParams();
    const [estimate, setEstimate] = useState(null);
    const [project, setProject] = useState(null);
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejecting, setRejecting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.get(`projects/${projectId}`).then(r => setProject(r.data)).catch(console.error);
        api.get(`estimates/by_project/${projectId}`).then(r => setEstimate(r.data)).catch(console.error);
    }, [projectId]);

    if (!estimate || !project) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    const contractor = project.contractor;
    const customer = project.customer;
    const condition = project.condition;

    const subtotal = estimate.breakdown?.length > 0
        ? estimate.breakdown.reduce((sum, row) => sum + Math.round((Number(row.quantity) || 0) * (Number(row.unit_price) || 0)), 0)
        : Math.round(estimate.amount / 1.1);
    const tax = estimate.breakdown?.length > 0
        ? Math.round(subtotal * 0.1)
        : estimate.amount - subtotal;
    const totalAmount = estimate.amount;

    const rowAmount = (row) => row.amount ?? Math.round((Number(row.quantity) || 1) * (Number(row.unit_price) || 0));

    const customerName = `${customer?.last_name || ''} ${customer?.first_name || ''}`.trim();
    const customerPostal = customer?.postal_code || '';
    const customerAddress = `${customer?.prefecture || ''}${customer?.city || ''}${customer?.address || ''}${customer?.building ? `　${customer.building}` : ''}`;
    const subjectText = condition
        ? `${customerName} 様　${condition.work_types?.join('・') || ''}${condition.location_city ? `（${condition.location_city}）` : ''}`
        : `${customerName} 様`;

    const emptyRowCount = Math.max(0, 5 - (estimate.breakdown?.length || 0));

    return (
        <div style={{ minHeight: '100vh', background: '#f5f6f8', padding: '32px 0 60px' }}>
            <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 16px' }}>

                {/* ツールバー（印刷時非表示） */}
                <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <button
                        onClick={() => navigate(-1)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#005c53', fontWeight: 500, fontSize: '0.9rem' }}
                    >
                        <ArrowLeft /> 戻る
                    </button>
                    <button
                        onClick={() => window.print()}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#fff', color: '#005c53', border: '2px solid #005c53', borderRadius: 4, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
                    >
                        <PrinterIcon /> 印刷する
                    </button>
                </div>

                {/* 書類本体 */}
                <div className="print-area ev-doc" style={{ background: '#fff', borderRadius: 8, padding: '48px 44px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', fontSize: '14px', lineHeight: 1.6, color: '#1a1a2e' }}>

                    {/* タイトル */}
                    <div style={{ textAlign: 'center', marginBottom: 32 }}>
                        <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>見積書</h1>
                    </div>

                    {/* 顧客情報（左）＋ 見積メタ情報（右） */}
                    <div className="ev-header-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24, gap: 16 }}>
                        <div style={{ flex: 1 }}>
                            <p style={{ fontWeight: 700, margin: '0 0 2px', fontSize: '16px' }}>{customerName} 様</p>
                            {customerPostal && <p style={{ color: '#6b7280', margin: 0, fontSize: '12px' }}>〒{customerPostal}</p>}
                            {customerAddress && <p style={{ color: '#6b7280', margin: '0 0 12px', fontSize: '12px' }}>{customerAddress}</p>}
                            <p style={{ margin: 0, fontSize: '13px' }}>件名: {subjectText}</p>
                        </div>
                        <div style={{ textAlign: 'right', fontSize: '13px', flexShrink: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginBottom: 4 }}>
                                <span style={{ color: '#6b7280' }}>見積日</span>
                                <span>{fmtDate(estimate.created_at)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginBottom: 4 }}>
                                <span style={{ color: '#6b7280' }}>見積番号</span>
                                <span>{docNo('Z', estimate.id, estimate.created_at)}</span>
                            </div>
                            {estimate.expires_at && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
                                    <span style={{ color: '#6b7280' }}>有効期限</span>
                                    <span>{fmtDate(estimate.expires_at)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 業者情報（右寄せ）＋ 印鑑 */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 28 }}>
                        <div style={{ textAlign: 'right' }}>
                            <p style={{ fontWeight: 700, margin: '0 0 2px', fontSize: '13px' }}>{contractor?.company_name || ''}</p>
                            <p style={{ color: '#6b7280', margin: 0, fontSize: '12px' }}>担当：{contractor?.representative || ''}</p>
                            {contractor?.postal_code && <p style={{ color: '#6b7280', margin: 0, fontSize: '12px' }}>〒{contractor.postal_code}</p>}
                            <p style={{ color: '#6b7280', margin: '0 0 8px', fontSize: '12px' }}>{contractor?.prefecture}{contractor?.city}{contractor?.address}</p>
                            {contractor?.seal_image ? (
                                <img src={`${storageBaseUrl}/${contractor.seal_image}`} alt="印鑑" style={{ width: 52, height: 52, objectFit: 'contain', opacity: 0.82, mixBlendMode: 'multiply' }} />
                            ) : (
                                <div style={{ display: 'inline-flex', width: 48, height: 48, border: '2px solid #ef4444', borderRadius: '50%', alignItems: 'center', justifyContent: 'center', color: '#ef4444', fontSize: '11px' }}>印</div>
                            )}
                        </div>
                    </div>

                    {/* 金額サマリーボックス */}
                    <div style={{ border: '2px solid #1a1a2e', borderRadius: 4, padding: '16px', marginBottom: 28, background: '#f9fafb' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontSize: '13px' }}>
                                <div style={{ display: 'flex', gap: 32, marginBottom: 4 }}>
                                    <span style={{ color: '#6b7280' }}>小計</span>
                                    <span style={{ fontWeight: 500 }}>{fmt(subtotal)}円</span>
                                </div>
                                <div style={{ display: 'flex', gap: 32 }}>
                                    <span style={{ color: '#6b7280' }}>消費税</span>
                                    <span style={{ fontWeight: 500 }}>{fmt(tax)}円</span>
                                </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '26px', fontWeight: 700, color: '#1a1a2e' }}>{fmt(totalAmount)}円</div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>見積金額</div>
                            </div>
                        </div>
                    </div>

                    {/* 明細テーブル */}
                    <div style={{ border: '1px solid #1a1a2e', marginBottom: 16, overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #1a1a2e' }}>
                                    {[['摘要', 'left', ''], ['数量', 'center', '64px'], ['単位', 'center', '52px'], ['単価（円）', 'right', '96px'], ['金額', 'right', '104px']].map(([h, align, w]) => (
                                        <th key={h} style={{
                                            padding: '10px 12px',
                                            textAlign: align,
                                            background: '#f9fafb',
                                            fontWeight: 600,
                                            borderRight: h !== '金額' ? '1px solid #1a1a2e' : 'none',
                                            width: w || 'auto',
                                            color: '#1a1a2e',
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {estimate.breakdown?.length > 0 ? estimate.breakdown.map((row, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #e0e4ea' }}>
                                        <td style={{ padding: '10px 12px', borderRight: '1px solid #e0e4ea' }}>{row.name || '—'}</td>
                                        <td style={{ padding: '10px 12px', textAlign: 'center', borderRight: '1px solid #e0e4ea' }}>{row.quantity ?? '—'}</td>
                                        <td style={{ padding: '10px 12px', textAlign: 'center', borderRight: '1px solid #e0e4ea' }}>{row.unit || '—'}</td>
                                        <td style={{ padding: '10px 12px', textAlign: 'right', borderRight: '1px solid #e0e4ea' }}>{fmt(row.unit_price)}</td>
                                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>{fmt(rowAmount(row))}</td>
                                    </tr>
                                )) : (
                                    <tr style={{ borderBottom: '1px solid #e0e4ea' }}>
                                        <td colSpan={5} style={{ padding: '10px 12px', textAlign: 'center', color: '#9ca3af' }}>—</td>
                                    </tr>
                                )}
                                {Array.from({ length: emptyRowCount }).map((_, i) => (
                                    <tr key={`e${i}`} style={{ borderBottom: '1px solid #e0e4ea' }}>
                                        {[...Array(5)].map((_, j) => (
                                            <td key={j} style={{ padding: '10px 12px', borderRight: j < 4 ? '1px solid #e0e4ea' : 'none' }}>&nbsp;</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* 消費税行 */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
                        <div className="ev-tax-row" style={{ border: '1px solid #e0e4ea', width: 300, fontSize: '13px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px' }}>
                                <span style={{ color: '#6b7280' }}>消費税</span>
                                <span>10%対象　{fmt(subtotal)}円</span>
                                <span>{fmt(tax)}円</span>
                            </div>
                        </div>
                    </div>

                    {/* 備考 */}
                    {estimate.note && (
                        <div style={{ paddingTop: 16, borderTop: '1px solid #e0e4ea' }}>
                            <p style={{ fontWeight: 700, margin: '0 0 6px', fontSize: '13px' }}>備考</p>
                            <p style={{ color: '#6b7280', whiteSpace: 'pre-wrap', fontSize: '12px', margin: 0 }}>{estimate.note}</p>
                        </div>
                    )}
                </div>

                {/* アクションボタン（印刷時非表示） */}
                {project.status === 'estimated' && (
                    <div className="no-print" style={{ marginTop: 20 }}>
                        <button
                            onClick={() => navigate(`/customer/decision/${projectId}`)}
                            style={{ width: '100%', padding: '14px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 700, fontSize: '1rem', cursor: 'pointer', marginBottom: 12 }}
                        >
                            この業者に発注する
                        </button>

                        {/* 差し戻しボタン */}
                        {!showRejectForm ? (
                            <button
                                onClick={() => setShowRejectForm(true)}
                                style={{ width: '100%', padding: '12px', background: '#fff', color: '#6b7280', border: '1px solid #e0e4ea', borderRadius: 4, fontWeight: 500, fontSize: '0.9rem', cursor: 'pointer' }}
                            >
                                内容について修正をお願いする
                            </button>
                        ) : (
                            <div style={{ border: '1px solid #fbbf24', borderRadius: 8, padding: '16px', background: '#fffbeb' }}>
                                <p style={{ fontWeight: 600, fontSize: '0.9rem', color: '#92400e', margin: '0 0 8px' }}>
                                    修正のご依頼
                                </p>
                                <p style={{ fontSize: '0.84rem', color: '#6b7280', margin: '0 0 12px' }}>
                                    修正してほしい内容をお知らせください（任意）。業者に通知されます。
                                </p>
                                <textarea
                                    value={rejectReason}
                                    onChange={e => setRejectReason(e.target.value)}
                                    placeholder="例：材料費の内訳をもう少し詳しく教えてください"
                                    rows={3}
                                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e0e4ea', borderRadius: 6, fontSize: '0.875rem', resize: 'none', boxSizing: 'border-box', outline: 'none', color: '#1a1a2e' }}
                                />
                                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                    <button
                                        onClick={() => { setShowRejectForm(false); setRejectReason(''); }}
                                        style={{ flex: 1, padding: '10px', background: '#fff', color: '#6b7280', border: '1px solid #e0e4ea', borderRadius: 4, fontSize: '0.875rem', cursor: 'pointer' }}
                                    >
                                        キャンセル
                                    </button>
                                    <button
                                        onClick={async () => {
                                            setRejecting(true);
                                            try {
                                                await api.post(`estimates/${estimate.id}/reject`, { rejection_reason: rejectReason || null });
                                                navigate(`/customer/chat/${projectId}`);
                                            } catch { setRejecting(false); }
                                        }}
                                        disabled={rejecting}
                                        style={{ flex: 1, padding: '10px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.875rem', fontWeight: 600, cursor: rejecting ? 'default' : 'pointer' }}
                                    >
                                        {rejecting ? '送信中...' : '修正を依頼する'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <style>{`
                @media (max-width: 600px) {
                    .ev-doc { padding: 24px 16px !important; }
                    .ev-header-row { flex-direction: column !important; }
                    .ev-header-row > div:last-child { text-align: left !important; }
                    .ev-header-row > div:last-child > div { justify-content: flex-start !important; }
                    .ev-tax-row { width: 100% !important; }
                }
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; }
                    @page { size: A4; margin: 15mm; }
                }
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

export default EstimateViewPage;
